/**
 * Proof-of-bank-account upload (AML control).
 *
 * Rails stored this document via ActiveStorage
 * (`Profile::BankAccount has_one_attached :proof_of_bank_account_document`) and
 * this module used to POST to `/rails/active_storage/direct_uploads`. That
 * endpoint 404s now Rails is stopped, AND the old code threw the returned
 * `signed_id` away, so even while it "worked" the uploaded blobs were orphaned
 * with nothing linking them to a bank account.
 *
 * The serverless replacement is a two-step, ActiveStorage-shaped flow:
 *
 *   1. POST /bank_accounts/proof_upload with the same `blob` fields Rails' direct
 *      upload took (filename / content_type / byte_size / checksum) and receive a
 *      server-minted object `key` plus a presigned S3 PUT url and the exact
 *      headers to send.
 *   2. PUT the raw File to that url. The signature binds Content-Type,
 *      Content-Length and Content-MD5, so S3 itself rejects anything else.
 *
 * The caller MUST then pass the returned `key` to POST /bank_accounts as
 * `proof_key` — that is what writes the `active_storage_attachments` row and
 * makes the document visible to the admin approving the account. Uploading
 * without linking is exactly the bug this replaces.
 */
import SparkMD5 from 'spark-md5'

import { requestBankAccountProofUpload } from 'api/compliance'
import { ApiError } from 'api/errors'

/** Max proof file size (10 MB). Mirrors the backend cap (PROOF_MAX_BYTES). */
export const MAX_PROOF_FILE_SIZE_BYTES = 10 * 1024 * 1024

/**
 * Content types the backend accepts (services/active_storage.py
 * PROOF_ALLOWED_CONTENT_TYPES). Rails accepted `image/*` or `application/pdf`;
 * the serverless allow-list is marginally narrower (no image/svg+xml) so a
 * customer-writable bucket cannot hold script-bearing content. Mirrored here so
 * an unsupported file gets the friendly client-side message instead of a 422.
 */
export const ACCEPTED_PROOF_CONTENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/tiff',
  'image/bmp'
] as const

/**
 * Extension fallback for browsers that report an empty `File.type` (common for
 * HEIC/HEIF off an iPhone, and for some PDFs in older Android WebViews).
 */
const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  bmp: 'image/bmp'
}

const EXTENSION_BY_CONTENT_TYPE: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/tiff': 'tiff',
  'image/bmp': 'bmp'
}

/**
 * Thrown for any failure of the upload leg. `message` is safe to show a
 * customer; `cause` keeps the underlying ApiError/network error for debugging.
 */
export class ProofUploadError extends Error {
  readonly cause?: unknown

  constructor(message: string, cause?: unknown) {
    super(message)
    this.name = 'ProofUploadError'
    // Restore the prototype chain — TS targeting ES5 breaks `instanceof` on
    // subclassed built-ins otherwise.
    Object.setPrototypeOf(this, ProofUploadError.prototype)
    this.cause = cause
  }
}

export interface ProofUploadResult {
  /** Server-minted ActiveStorage key — pass to createBankAccount as proof_key. */
  key: string
  /** Sanitised filename the backend will store on the blob row. */
  filename: string
}

/**
 * Normalised, backend-accepted content type for `file`, or null when the file is
 * not a PDF/supported image. Falls back to the extension when the browser
 * reports no type at all.
 */
export function resolveProofContentType(file: File): string | null {
  const declared = (file.type || '').split(';')[0].trim().toLowerCase()
  if ((ACCEPTED_PROOF_CONTENT_TYPES as readonly string[]).includes(declared)) {
    return declared
  }
  if (declared) return null

  const ext = (file.name.split('.').pop() || '').toLowerCase()
  return EXTENSION_CONTENT_TYPES[ext] ?? null
}

/**
 * Read a File as an ArrayBuffer. `Blob.arrayBuffer()` is the fast path but is
 * missing on Safari < 14 and older Android WebViews (and on jsdom), so fall back
 * to FileReader rather than throwing "file.arrayBuffer is not a function" at a
 * customer mid-onboarding.
 */
function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === 'function') {
    return file.arrayBuffer()
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = () =>
      reject(reader.error ?? new Error('Could not read the selected file'))
    reader.readAsArrayBuffer(file)
  })
}

/**
 * Compute base64-encoded MD5 checksum of file, as ActiveStorage requires. The
 * backend binds it into the presigned PUT as Content-MD5, so S3 rejects the
 * upload if the bytes do not match what was declared.
 */
export async function fileChecksumBase64(file: File): Promise<string> {
  const arrayBuffer = await readFileAsArrayBuffer(file)
  const hexHash = SparkMD5.ArrayBuffer.hash(arrayBuffer)
  const bytes = new Uint8Array(16)
  for (let i = 0; i < 32; i += 2) {
    bytes[i / 2] = parseInt(hexHash.substr(i, 2), 16)
  }
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

/**
 * Short, safe filename. Kept from the original implementation so the blob rows
 * this writes look like the ones already in production ("bank_statement.pdf")
 * and long/odd customer filenames never reach the storage layer.
 */
function proofFilename(contentType: string, file: File): string {
  const rawExt = (file.name.split('.').pop() || '').replace(/[^a-z0-9]/gi, '')
  const ext =
    EXTENSION_BY_CONTENT_TYPE[contentType] || rawExt.toLowerCase() || 'pdf'
  return `bank_statement.${ext}`
}

const UPLOAD_RETRY_MESSAGE =
  'We could not upload your bank statement. Please try again.'
const UPLOAD_CONNECTION_MESSAGE =
  'We could not upload your bank statement. Please check your connection and try again.'

function presignErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    // 422 is a schema rejection (type/size/checksum). The FastAPI body is a
    // validation-error list, not a customer-readable sentence.
    if (err.status === 422) {
      return 'We could not accept that file. Please upload a PDF or image (e.g. PNG, JPEG) under 10 MB.'
    }
    if (err.code === 'NETWORK_ERROR') {
      return UPLOAD_CONNECTION_MESSAGE
    }
    // Domain failure — the backend envelope message is written for customers.
    if (err.message) return err.message
  }
  return UPLOAD_RETRY_MESSAGE
}

/**
 * Upload the proof document and return the key needed to link it to the bank
 * account.
 *
 * Throws ProofUploadError with a customer-safe message on any failure. The
 * caller must NOT create the bank account when this rejects — an account with no
 * proof on file is the AML gap this exists to close.
 */
export async function uploadProofFile(file: File): Promise<ProofUploadResult> {
  const contentType = resolveProofContentType(file)
  if (contentType === null) {
    throw new ProofUploadError(
      'Please select a PDF or image file (e.g. PNG, JPEG)'
    )
  }
  if (file.size > MAX_PROOF_FILE_SIZE_BYTES) {
    throw new ProofUploadError(
      `File is too large. Maximum size is ${
        MAX_PROOF_FILE_SIZE_BYTES / 1024 / 1024
      } MB.`
    )
  }
  if (file.size === 0) {
    throw new ProofUploadError(
      'That file is empty. Please choose another file.'
    )
  }

  const checksum = await fileChecksumBase64(file)
  const filename = proofFilename(contentType, file)

  let slot
  try {
    slot = await requestBankAccountProofUpload({
      filename,
      content_type: contentType,
      byte_size: file.size,
      checksum
    })
  } catch (err) {
    throw new ProofUploadError(presignErrorMessage(err), err)
  }

  if (!slot?.url || !slot?.key) {
    throw new ProofUploadError(UPLOAD_RETRY_MESSAGE, slot)
  }

  // Direct browser PUT to S3. Deliberately plain fetch, not apiClient: the URL
  // is presigned, so an Authorization header would make S3 reject it ("only one
  // auth mechanism allowed"), and the axios baseURL/interceptors do not apply.
  // Send exactly the headers the backend signed — no more, no fewer.
  let response: Response
  try {
    response = await fetch(slot.url, {
      method: 'PUT',
      body: file,
      headers: slot.headers
    })
  } catch (err) {
    throw new ProofUploadError(UPLOAD_CONNECTION_MESSAGE, err)
  }

  if (!response.ok) {
    throw new ProofUploadError(
      UPLOAD_RETRY_MESSAGE,
      new Error(`S3 upload failed: ${response.status} ${response.statusText}`)
    )
  }

  return { key: slot.key, filename: slot.filename || filename }
}
